import {
  useAppService,
  useSaveProductPrices,
  useSelectedOrgId,
} from "@fero/hooks";
import { fileToString } from "@fero/utils";
import type { ProductSalePriceInfo } from "@fero-shared/api-contracts";
import type { ParseResult } from "papaparse";
import { useCallback, useState } from "react";
import { usePapaParse } from "react-papaparse";
import type { RowIdentifierAndErrors, WithCsvRowNumber } from "utils/csv-utils";
import { assertNotUndefined } from "utils/hx/util/types";
import {
  ProductSalePriceCsvParser,
  type ProductSalePriceRowData,
} from "./product-sale-price-csv-parser";

/**
 * A custom hook to handle most of the import sale price logic - we can probably
 * make this more generic long term if we would like to use it with other imports
 */
export const useImportSalePrices = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File>();
  const [parseErrors, setParseErrors] = useState<
    WithCsvRowNumber<RowIdentifierAndErrors>[]
  >([]);
  const [parsedRowData, setParsedRowData] = useState<
    WithCsvRowNumber<ProductSalePriceRowData>[]
  >([]);

  const organisationId = assertNotUndefined(useSelectedOrgId());
  const service = useAppService();
  const saveProductPricesMutation = useSaveProductPrices();
  const { readString } = usePapaParse();

  /*
   * Handles parsing the CSV and turning it into validated data
   */
  const onParseCsv = async (parseResult: ParseResult<string[]>) => {
    const trimmedData = parseResult.data.map((row) => row.map((s) => s.trim()));
    const header = trimmedData[0]?.map((s) => s?.trim());
    const body = trimmedData.slice(1);

    if (!body || body.length === 0) {
      return {
        kind: "errors",
        value: [{ rowNumber: 0, value: { errors: ["No data found."] } }],
      };
    }

    const csvParser = new ProductSalePriceCsvParser(header);
    // TODO - Implement dynamic currency
    const result = await csvParser.process(
      body,
      service,
      organisationId,
      "GBP",
    );

    if (result.kind === "errors") {
      setParseErrors(result.value);
      return;
    }
    setParsedRowData(result.value);
  };

  const saveProductSalePrices = useCallback(
    async (csvSalePrices: ProductSalePriceRowData[]) => {
      const salePrices: ProductSalePriceInfo[] = csvSalePrices
        .map((salePrice) => {
          // If we can't map to one of our products, don't include in the upload
          if (!salePrice.productId) {
            return undefined;
          }

          return {
            productId: salePrice.productId,
            productCode: salePrice.productCode,
            currency: salePrice.currency,
            incDutyExVatPrice: salePrice.incDutyExVatPrice?.toFixed(2) ?? null,
            incDutyAndVatPrice:
              salePrice.incDutyAndVatPrice?.toFixed(2) ?? null,
            exDutyAndVatPrice: salePrice.exDutyAndVatPrice?.toFixed(2) ?? null,
          };
        })
        .filter((p) => p !== undefined) as ProductSalePriceInfo[];

      await saveProductPricesMutation.mutateAsync({
        body: {
          organisationId: organisationId,
          purchaserId: null,
          salePrices,
        },
      });
    },
    [organisationId, saveProductPricesMutation],
  );

  const onSubmit = async () => {
    await saveProductSalePrices(parsedRowData.map((rowData) => rowData.value));

    setIsModalOpen(false);
    setSelectedFile(undefined);
    setParseErrors([]);
    setParsedRowData([]);
  };

  const onClose = async () => {
    setSelectedFile(undefined);
    setIsModalOpen(false);
    setParseErrors([]);
    setParsedRowData([]);
  };

  const onSelectedFileChange = async (file?: File) => {
    // Update state
    setSelectedFile(file);
    setParseErrors([]);
    setParsedRowData([]);

    if (file) {
      // Convert file > string
      const fileString = (await fileToString(file)) as string;

      // Convert string > parsed information
      readString(fileString, {
        worker: true,
        dynamicTyping: false,
        header: false,
        skipEmptyLines: "greedy",
        complete: onParseCsv,
      });
    }
  };

  return {
    isModalOpen,
    selectedFile,
    parseErrors,
    parsedRowData,
    onSubmit,
    onClose,
    onSelectedFileChange,
    setIsModalOpen,
  };
};
